import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  GridApi,
} from 'ag-grid-enterprise';
import { Molecule } from '../../types/chemistry';
import { StructureEditorModal } from './StructureEditor';
import StructureImage from './StructureImage';
import { chemistryApi } from '../../api/chemistry';
import LinkTargetModal from './LinkTargetModal';
import {
  convertValue, getCompatibleUnits, formatAxisLabel,
  getAvailableConversions, convertMolarToMass, convertMassToMolar,
  convertToNegLog, convertFromNegLog, getLogPartner, getMolarUnitFromLabel,
  type ConversionOption,
} from '../../utils/unitConversion';
import { inferUnits } from '../../utils/unitInference';
import { revealMolecule, WorkbenchFocus } from '../../utils/workbenchNavigation';


// Column statistics for heatmap
export interface ColumnStats {
  [columnName: string]: {
    min: number;
    max: number;
    isNumeric: boolean;
  };
}

// Per-column heatmap settings
export interface HeatmapColumnConfig {
  enabled: boolean;
  min: number;  // Actual value threshold
  max: number;  // Actual value threshold
}

export interface HeatmapSettings {
  intensity: number;  // Opacity: 0-1
  columns: {
    [columnName: string]: HeatmapColumnConfig;
  };
}

interface MoleculeGridProps {
  fileId: string;
  fileName?: string;  // For "Back to File" link in Knowledge Centre
  isLoading?: boolean;
  highlightedIds?: Set<number>;
  searchQuery?: string;
  onSelectionChange?: (selectedMolecules: Molecule[]) => void;
  onMoleculeUpdate?: () => void;
  clientData?: Record<string, any>[];
  clientColumns?: ColDef[];
  isTransposed?: boolean;  // When true, use taller headers for structure thumbnails
  showHeatmap?: boolean;
  heatmapSettings?: HeatmapSettings;
  onColumnStatsChange?: (stats: ColumnStats) => void;  // Expose column stats to parent
  matchIds?: Set<number> | null;  // IDs of molecules matching current search - for SSRM filtering
  refreshTrigger?: number;  // Increment to trigger SSRM refresh (e.g., after patent enrichment)
  isLocked?: boolean;  // When true, cells are not editable
  onCellEdit?: (moleculeId: number, propertyKey: string, oldValue: any, newValue: any) => void;  // Callback for undo history
  resetColumnsTrigger?: number;  // Increment to reset columns to default widths
  selectAllTrigger?: number;  // Increment to select all rows
  deselectAllTrigger?: number;  // Increment to deselect all rows
  onRowClick?: (molecule: Molecule, tab?: string) => void;  // Open compound detail panel
  selectedCompoundId?: number | null;  // Currently selected compound in detail panel
  navigationFocus?: WorkbenchFocus | null;
  columnUnitsMap?: Record<string, { unit: string | null; unitType: string | null; displayName: string | null }>;
  onUnitOverridesChange?: (overrides: Record<string, { originalUnit: string; displayUnit: string; unitType: string }>) => void;
  /** Override the default SSRM query function. When provided, used instead of chemistryApi.queryMolecules. */
  ssrmQueryFn?: (params: { startRow: number; endRow: number; sortModel?: Array<{ colId: string; sort: string }>; filterModel?: Record<string, any>; matchIds?: number[] }) => Promise<{ rows: Molecule[]; lastRow: number }>;
  /** Column visibility overrides — key is property name, value is visible. Increment trigger to apply. */
  columnVisibilityTrigger?: number;
  columnVisibilityAction?: { columnId: string; visible: boolean } | null;
  /** Callback fired when grid API is ready — parent can use it to query live column state */
  onGridApiReady?: (api: GridApi) => void;
}

// Column state persistence utilities
const COLUMN_STATE_KEY_PREFIX = 'columnState_';

const saveColumnState = (fileId: string, state: any[]) => {
  try {
    localStorage.setItem(`${COLUMN_STATE_KEY_PREFIX}${fileId}`, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save column state:', e);
  }
};

const loadColumnState = (fileId: string): any[] | null => {
  try {
    const saved = localStorage.getItem(`${COLUMN_STATE_KEY_PREFIX}${fileId}`);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.warn('Failed to load column state:', e);
    return null;
  }
};

const clearColumnState = (fileId: string) => {
  try {
    localStorage.removeItem(`${COLUMN_STATE_KEY_PREFIX}${fileId}`);
  } catch (e) {
    console.warn('Failed to clear column state:', e);
  }
};

const svgCache = new Map<string, string>();
const pngCache = new Map<number, string>();  // moleculeId -> blob URL

const StructureRenderer: React.FC<{
  smiles: string;
  molfile?: string;
  moleculeId?: number;
  highlightQuery?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
}> = ({ smiles, molfile, moleculeId, highlightQuery, onClick }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const [pngUrl, setPngUrl] = useState<string | null>(
    moleculeId ? pngCache.get(moleculeId) ?? null : null
  );
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [pngFailed, setPngFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const structureValue = molfile || smiles;
  const structureType = molfile ? 'MOL' : 'RAW';
  const cacheKey = structureValue ? `${structureValue.substring(0, 100)}|${highlightQuery || ''}` : '';

  // Use cached PNG when molecule ID is available and no highlight query
  const usePng = !!(moleculeId && !highlightQuery && !pngFailed);

  // Check SVG cache immediately for instant render if available
  const cachedSvg = cacheKey ? svgCache.get(cacheKey) : null;

  // Intersection Observer - only fetch when cell becomes visible
  useEffect(() => {
    if (pngUrl || cachedSvg || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '300px' }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [pngUrl, cachedSvg]);

  // Fetch PNG via authenticated API client when visible
  useEffect(() => {
    if (!usePng || !isVisible || pngUrl || !moleculeId) return;

    let cancelled = false;
    setLoading(true);
    const fetchPng = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const response = await apiClient.get(`/molecules/${moleculeId}/png`, {
          responseType: 'blob'
        });
        if (cancelled) return;
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        pngCache.set(moleculeId, url);
        setPngUrl(url);
      } catch {
        if (!cancelled) setPngFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPng();
    return () => { cancelled = true; };
  }, [usePng, isVisible, moleculeId, pngUrl]);

  // Fetch SVG fallback — for highlight queries or when PNG fails
  useEffect(() => {
    if (usePng || !isVisible || !structureValue || cachedSvg) return;

    setLoading(true);
    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const renderParams: any = {
          structure: { value: structureValue, type: structureType },
          width: '200px',
          height: '150px'
        };

        if (highlightQuery) {
          renderParams.query = { value: highlightQuery, type: 'RAW' };
        }

        const response = await apiClient.post('/v2/indigo/render', renderParams, {
          responseType: 'text'
        });
        const svgData = response.data;
        svgCache.set(cacheKey, svgData);
        setSvg(svgData);
      } catch (err) {
        console.error('Failed to render structure:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSvg();
  }, [usePng, isVisible, structureValue, structureType, highlightQuery, cacheKey, cachedSvg]);

  // Context menu handler for copying SMILES
  const handleContextMenu = (e: React.MouseEvent) => {
    if (!smiles) return;
    e.preventDefault();
    navigator.clipboard.writeText(smiles);
    const el = e.currentTarget as HTMLElement;
    el.title = 'SMILES copied!';
    setTimeout(() => { el.title = 'Click to edit in Ketcher'; }, 1500);
  };

  // PNG path: show cached or fetched PNG
  if (pngUrl) {
    return (
      <div
        ref={containerRef}
        className="relative flex items-center justify-center h-full w-full cursor-pointer hover:bg-blue-50 overflow-hidden structure-cell"
        title="Click to edit in Ketcher"
        onClick={onClick}
        onContextMenu={handleContextMenu}
      >
        <img
          src={pngUrl}
          alt={smiles}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  }

  // Use cached SVG if available
  const displaySvg = cachedSvg || svg;

  // Show SVG if we have it (cached or fetched)
  if (displaySvg) {
    return (
      <div
        ref={containerRef}
        className="relative flex items-center justify-center h-full w-full cursor-pointer hover:bg-blue-50 overflow-hidden structure-cell"
        title="Click to edit in Ketcher"
        onClick={onClick}
        onContextMenu={handleContextMenu}
      >
        <div className="w-full h-full [&>svg]:max-w-full [&>svg]:max-h-full flex items-center justify-center" dangerouslySetInnerHTML={{ __html: displaySvg }} />
      </div>
    );
  }

  // Show loading spinner when actively fetching
  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full w-full structure-cell">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show SMILES placeholder until visible (lazy load)
  if (!structureValue) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full w-full text-xs text-gray-400 structure-cell">
        No structure
      </div>
    );
  }

  // Placeholder: show StructureImage until grid's own fetch completes
  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center h-full w-full cursor-pointer hover:bg-blue-50 structure-cell"
      title="Click to edit in Ketcher"
      onClick={onClick}
    >
      <StructureImage smiles={smiles} moleculeId={moleculeId} size={80} lazy />
    </div>
  );
};

// Actions renderer — single info button to open compound detail panel
const ActionsRenderer: React.FC<{
  data: Molecule;
  context?: { onViewDetails?: (molecule: Molecule) => void; onRowClick?: (molecule: Molecule, tab?: string) => void; fileId?: string; fileName?: string };
}> = ({ data, context }) => {
  const molecule = data;
  if (!molecule) return null;

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (context?.onRowClick) {
      context.onRowClick(molecule);
    }
  };

  return (
    <div className="flex items-center justify-start">
      <button
        onClick={handleInfoClick}
        className="p-1.5 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded transition-colors"
        title="View compound details — targets, pathways, workbench tools, and patent data"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
    </div>
  );
};

// Entity Type Badge configuration
const ENTITY_BADGES: Record<string, { label: string; color: string }> = {
  small_molecule: { label: 'Small Mol', color: '#0d9488' },
  peptide: { label: 'Peptide', color: '#7c3aed' },
  protein: { label: 'Protein', color: '#2563eb' },
  antibody: { label: 'Antibody', color: '#dc2626' },
  adc: { label: 'ADC', color: '#ea580c' },
  nucleotide: { label: 'Nucleotide', color: '#0891b2' },
  oligonucleotide: { label: 'Oligo', color: '#4f46e5' },
  plasmid: { label: 'Plasmid', color: '#059669' },
  polymer: { label: 'Polymer', color: '#b45309' },
  macrocycle: { label: 'Macrocycle', color: '#be185d' },
};

const EntityTypeBadge: React.FC<{ value: string }> = ({ value }) => {
  const badge = ENTITY_BADGES[value] || ENTITY_BADGES.small_molecule;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
      }}
      title={badge.label}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: badge.color,
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>
        {badge.label}
      </span>
    </span>
  );
};

/**
 * Find the best display name for a molecule from its properties.
 * Checks fields in priority order (case-insensitive), returns first match or fallback.
 */
export function getMoleculeName(properties: Record<string, any> | undefined | null): string | null {
  if (!properties) return null;

  const priorityFields = [
    'Name', 'Molecule', 'Molecule Name', 'MoleculeName', 'Molecule_Name',
    'Compound', 'Compound Name', 'CompoundName', 'Compound_Name',
    'Title', 'Label',
    'ID', 'Mol ID', 'MolID', 'Mol_ID', 'Molecule ID', 'MoleculeID',
    'Compound ID', 'CompoundID', 'Compound_ID',
    'Structure', 'Structure Name',
    'CAS', 'CAS Number', 'Registry Number',
    'Catalog Number', 'Batch', 'Lot',
  ];

  const lowerMap: Record<string, string> = {};
  for (const key of Object.keys(properties)) {
    lowerMap[key.toLowerCase()] = key;
  }

  for (const field of priorityFields) {
    const actualKey = lowerMap[field.toLowerCase()];
    if (actualKey) {
      const value = properties[actualKey];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
  }

  return null;
}

const MoleculeGrid: React.FC<MoleculeGridProps> = ({
  fileId,
  fileName,
  isLoading,
  highlightedIds,
  searchQuery,
  onSelectionChange,
  onMoleculeUpdate,
  clientData,
  clientColumns,
  isTransposed,
  showHeatmap,
  heatmapSettings = { intensity: 0.4, columns: {} },
  onColumnStatsChange,
  matchIds,
  refreshTrigger,
  isLocked = true,
  onCellEdit,
  resetColumnsTrigger,
  selectAllTrigger,
  deselectAllTrigger,
  onRowClick,
  selectedCompoundId,
  navigationFocus,
  columnUnitsMap,
  onUnitOverridesChange,
  ssrmQueryFn,
  columnVisibilityTrigger,
  columnVisibilityAction,
  onGridApiReady,
}) => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const navigationFocusRef = useRef(navigationFocus);
  navigationFocusRef.current = navigationFocus;
  const preparedFocusRef = useRef<string | null>(null);
  const completedFocusRef = useRef<string | null>(null);
  const revealingFocusRef = useRef(false);

  const revealNavigationFocus = useCallback(() => {
    const api = gridApiRef.current, focus = navigationFocusRef.current;
    if (!api || !focus || revealingFocusRef.current || completedFocusRef.current === focus.requestKey) return;
    if (preparedFocusRef.current !== focus.requestKey) return;
    revealingFocusRef.current = true;
    try {
      if (revealMolecule(api, focus)) completedFocusRef.current = focus.requestKey;
    } finally { revealingFocusRef.current = false; }
  }, []);
  const [selectedMolecule, setSelectedMolecule] = useState<Molecule | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [propertyColumns, setPropertyColumns] = useState<string[]>([]);
  const [columnStats, setColumnStats] = useState<ColumnStats>({});
  const [unitOverrides, setUnitOverrides] = useState<Record<string, { originalUnit: string; displayUnit: string; unitType: string }>>({});
  const [entityTypeMenu, setEntityTypeMenu] = useState<{ x: number; y: number; moleculeId: number; currentType: string } | null>(null);
  const [entityTypeCounts, setEntityTypeCounts] = useState<Record<string, number>>({});
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null);

  // Derived columns: columns added via "Add Converted Column"
  interface DerivedColumn {
    id: string;           // unique key e.g. "IC50__pIC50"
    sourceField: string;  // e.g. "IC50"
    headerName: string;   // e.g. "pIC50 (from IC50)"
    targetUnit: string;   // e.g. "pIC50" or "µM"
    type: 'linear' | 'cross_system' | 'logarithmic';
    needsMW: boolean;
    sourceUnit: string;   // original unit of source column
    sourceUnitType: string;
  }
  const [derivedColumns, setDerivedColumns] = useState<DerivedColumn[]>([]);

  // Snapshot column widths before derived column changes, restore after
  const snapshotColumnWidths = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const widths: Record<string, number> = {};
    api.getColumns()?.forEach((col: any) => {
      widths[col.getColId()] = col.getActualWidth();
    });
    savedColumnWidthsRef.current = widths;
  }, []);

  // When derivedColumns count changes, restore old widths and autosize only new columns
  useEffect(() => {
    const api = gridApiRef.current;
    const prevCount = prevDerivedCountRef.current;
    const curCount = derivedColumns.length;
    prevDerivedCountRef.current = curCount;

    if (!api || prevCount === curCount) return; // No change, or initial render

    const saved = savedColumnWidthsRef.current;
    if (Object.keys(saved).length === 0) return; // Nothing saved

    // Wait for AG Grid to process the new columnDefs
    setTimeout(() => {
      // Restore widths for all previously-existing columns
      const stateUpdates: { colId: string; width: number }[] = [];
      api.getColumns()?.forEach((col: any) => {
        const colId = col.getColId();
        const savedWidth = saved[colId];
        if (savedWidth) {
          stateUpdates.push({ colId, width: savedWidth });
        }
      });

      if (stateUpdates.length > 0) {
        api.applyColumnState({ state: stateUpdates as any });
      }

      // Auto-size only new derived columns
      const newColIds = api.getColumns()
        ?.map((c: any) => c.getColId())
        .filter((id: string) => id.startsWith('derived.') && !saved[id]) || [];
      if (newColIds.length > 0) {
        api.autoSizeColumns(newColIds, false);
      }
    }, 50);
  }, [derivedColumns]);

  // Notify parent of unit override changes (for syncing with AnalyseView)
  useEffect(() => {
    onUnitOverridesChange?.(unitOverrides);
  }, [unitOverrides, onUnitOverridesChange]);

  const [targetCounts, setTargetCounts] = useState<Record<number, number>>({});
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMolecule, setLinkModalMolecule] = useState<Molecule | null>(null);
  const [ssrmFirstLoadDone, setSsrmFirstLoadDone] = useState(false);
  const [hasEnrichmentData, setHasEnrichmentData] = useState(false);

  // Edit history for undo functionality
  interface EditHistoryEntry {
    moleculeId: number;
    propertyKey: string;
    oldValue: any;
    newValue: any;
    rowData: any;
  }
  const editHistoryRef = useRef<EditHistoryEntry[]>([]);

  // Debounce timer for saving column state
  const columnResizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard: only auto-size columns once on first data render
  const hasAutoSizedRef = useRef(false);

  // Save column state before transpose so it can be restored when toggling back
  const preTransposeColumnStateRef = useRef<any[] | null>(null);
  const prevIsTransposedRef = useRef(isTransposed);

  // Track column widths so derived-column additions don't reset them
  const savedColumnWidthsRef = useRef<Record<string, number>>({});
  const prevDerivedCountRef = useRef(0);

  // Track fileId in a ref to avoid datasource recreation
  const fileIdRef = useRef(fileId);
  fileIdRef.current = fileId;

  // Track custom SSRM query function in a ref
  const ssrmQueryFnRef = useRef(ssrmQueryFn);
  ssrmQueryFnRef.current = ssrmQueryFn;

  // Track matchIds in a ref so getRows can access current search context
  const matchIdsRef = useRef<Set<number> | null | undefined>(matchIds);
  matchIdsRef.current = matchIds;

  // Track loadTargetCounts in a ref so getRows can access it
  const loadTargetCountsRef = useRef<((molecules: Molecule[]) => Promise<void>) | undefined>(undefined);

  // Track if we've extracted columns (use ref to avoid triggering datasource recreation)
  const columnsExtractedRef = useRef(false);

  const isClientSide = !!clientData;

  console.log('[DEBUG MoleculeGrid] isClientSide:', isClientSide, 'clientData:', clientData ? `array(${clientData.length})` : 'undefined');

  // Extract property columns from clientData when in client-side mode (search results)
  // This is needed because getRows (which normally extracts columns) is only called in SSRM mode
  useEffect(() => {
    if (isClientSide && !clientColumns && clientData && clientData.length > 0) {
      const keys = new Set<string>();
      clientData.forEach((mol: any) => {
        if (mol.properties) {
          Object.keys(mol.properties).forEach((key) => keys.add(key));
        }
      });
      const extractedCols = Array.from(keys);
      console.log('[MoleculeGrid] Extracted property columns from clientData:', extractedCols);
      if (extractedCols.length > 0) {
        setPropertyColumns(extractedCols);
      }

      // Detect enrichment data in client-side mode (same check as SSRM getRows)
      // Use truthy checks (not != null) so default DB zeros don't trigger columns
      if (!hasEnrichmentData && clientData.some((m: any) =>
        m.ip_status || m.patent_count_exact || m.patent_max_zone ||
        m.target_count || m.pathway_count || m.reaction_count || m.bioactivity_count || m.admet_flags || m.tox_alerts
      )) {
        setHasEnrichmentData(true);
      }
    }
  }, [isClientSide, clientColumns, clientData, hasEnrichmentData]);

  // Calculate column stats for heatmap (client-side mode)
  useEffect(() => {
    if (!isClientSide || !clientData || clientData.length === 0 || propertyColumns.length === 0) {
      return;
    }

    const stats: Record<string, { min: number; max: number; isNumeric: boolean }> = {};

    propertyColumns.forEach((key) => {
      let min = Infinity;
      let max = -Infinity;
      let hasNumeric = false;

      clientData.forEach((mol: any) => {
        const value = mol.properties?.[key];
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(numValue) && isFinite(numValue)) {
          hasNumeric = true;
          min = Math.min(min, numValue);
          max = Math.max(max, numValue);
        }
      });

      if (hasNumeric && min !== Infinity && max !== -Infinity) {
        stats[key] = { min, max, isNumeric: true };
      }
    });

    console.log('[Heatmap] Calculated column stats:', stats);
    setColumnStats(stats);
  }, [isClientSide, clientData, propertyColumns]);

  // Notify parent when columnStats changes
  useEffect(() => {
    if (onColumnStatsChange && Object.keys(columnStats).length > 0) {
      onColumnStatsChange(columnStats);
    }
  }, [columnStats, onColumnStatsChange]);

  const handleStructureClick = useCallback((molecule: Molecule) => {
    // Open Ketcher directly — no intermediate viewer
    setSelectedMolecule(molecule);
    setEditorOpen(true);
  }, []);

  const handleStructureDblClick = useCallback((molecule: Molecule) => {
    // Same as single click — open Ketcher
    setSelectedMolecule(molecule);
    setEditorOpen(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setEditorOpen(false);
    setSelectedMolecule(null);
  }, []);

  const handleSaveStructure = useCallback(async (molfile: string, smiles: string) => {
    if (!selectedMolecule || !fileIdRef.current) {
      setEditorOpen(false);
      setSelectedMolecule(null);
      return;
    }

    try {
      const { default: apiClient } = await import('../../api/client');
      await apiClient.put(`/index/${fileIdRef.current}/molecule`, {
        id: selectedMolecule.id,
        structure: molfile || smiles,
      });

      svgCache.clear();
      // Invalidate cached PNG so the grid re-fetches the freshly rendered image
      if (selectedMolecule.id && pngCache.has(selectedMolecule.id)) {
        URL.revokeObjectURL(pngCache.get(selectedMolecule.id)!);
        pngCache.delete(selectedMolecule.id);
      }
      if (onMoleculeUpdate) onMoleculeUpdate();
      if (gridApiRef.current) {
        gridApiRef.current.refreshServerSide({ purge: true });
      }

      setEditorOpen(false);
      setSelectedMolecule(null);
    } catch (error) {
      console.error('Failed to save molecule:', error);
      alert('Failed to save molecule. Please try again.');
    }
  }, [selectedMolecule, onMoleculeUpdate]);

  const onSelectionChanged = useCallback(() => {
    if (gridApiRef.current && onSelectionChange) {
      const selectedRows = gridApiRef.current.getSelectedRows();
      onSelectionChange(selectedRows);
    }
  }, [onSelectionChange]);

  // Load target counts for molecules
  const loadTargetCounts = useCallback(async (molecules: Molecule[]) => {
    const counts: Record<number, number> = {};
    await Promise.all(
      molecules.map(async (mol) => {
        try {
          const res = await chemistryApi.getMoleculeTargets(mol.id);
          counts[mol.id] = res.total;
        } catch {
          counts[mol.id] = 0;
        }
      })
    );
    setTargetCounts((prev) => ({ ...prev, ...counts }));
  }, []);

  // Update ref so datasource can access the function
  loadTargetCountsRef.current = loadTargetCounts;

  // Load target counts for client-side data
  useEffect(() => {
    if (isClientSide && clientData && clientData.length > 0) {
      loadTargetCounts(clientData as Molecule[]);
    }
  }, [isClientSide, clientData, loadTargetCounts]);

  const handleTargetLinkClick = useCallback((molecule: Molecule) => {
    setLinkModalMolecule(molecule);
    setLinkModalOpen(true);
  }, []);

  const handleViewDetails = useCallback((molecule: Molecule) => {
    navigate(`/molecule/${molecule.id}`);
  }, [navigate]);

  const handleCloseLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setLinkModalMolecule(null);
  }, []);

  const handleLinksUpdated = useCallback(() => {
    if (linkModalMolecule) {
      loadTargetCounts([linkModalMolecule]);
    }
  }, [linkModalMolecule, loadTargetCounts]);

  const handleEntityTypeChange = useCallback(async (moleculeId: number, newType: string) => {
    try {
      await chemistryApi.updateEntityType(moleculeId, newType);
      // Refresh the grid
      gridRef.current?.api?.refreshServerSide({ purge: false });
      setEntityTypeMenu(null);
    } catch (err) {
      console.error('Failed to update entity type:', err);
      setEntityTypeMenu(null);
    }
  }, []);

  // Linked targets renderer
  const LinkedTargetsRenderer = useCallback((params: any) => {
    const molecule = params.data as Molecule;
    if (!molecule) return null;
    const count = targetCounts[molecule.id] || 0;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleTargetLinkClick(molecule);
        }}
        className={`px-2 py-1 rounded text-xs font-medium ${
          count > 0
            ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        title={count > 0 ? `${count} linked target(s) - click to manage` : 'Click to link targets'}
      >
        {count > 0 ? `${count} target${count !== 1 ? 's' : ''}` : 'Link'}
      </button>
    );
  }, [targetCounts, handleTargetLinkClick]);

  const getRowStyle = useCallback((params: any) => {
    const molId = Number(params.data?.id);
    // Selected compound in detail panel - teal tint
    if (selectedCompoundId != null && molId === selectedCompoundId) {
      return { backgroundColor: 'rgba(13, 148, 136, 0.14)', boxShadow: 'inset 4px 0 0 #0d9488' };
    }
    // Search highlight - amber
    if (highlightedIds && highlightedIds.has(molId)) {
      return { backgroundColor: '#fef3c7', boxShadow: 'none' };
    }
    return { backgroundColor: '', boxShadow: 'none' };
  }, [highlightedIds, selectedCompoundId]);

  // Create datasource ONCE using useRef - no dependencies that change
  const datasourceRef = useRef<IServerSideDatasource | null>(null);

  if (!datasourceRef.current) {
    datasourceRef.current = {
      getRows: async (params: IServerSideGetRowsParams) => {
        const currentFileId = fileIdRef.current;
        console.log('[SSRM] getRows called:', {
          fileId: currentFileId,
          startRow: params.request.startRow,
          endRow: params.request.endRow,
        });

        if (!currentFileId) {
          console.error('[SSRM] No fileId available');
          params.fail();
          return;
        }

        try {
          // Convert matchIds Set to array for API (if active search)
          const currentMatchIds = matchIdsRef.current;
          const matchIdsArray = currentMatchIds ? Array.from(currentMatchIds) : undefined;
          console.log('[SSRM] Sending matchIds:', matchIdsArray?.length || 'none');

          const queryParams = {
            startRow: params.request.startRow || 0,
            endRow: params.request.endRow || 100,
            sortModel: params.request.sortModel?.length ? params.request.sortModel.map((s) => ({
              colId: s.colId,
              sort: s.sort,
            })) : navigationFocusRef.current ? [
              { colId: 'mol_index', sort: 'asc' as const },
              { colId: 'id', sort: 'asc' as const },
            ] : undefined,
            filterModel: params.request.filterModel || undefined,
            matchIds: matchIdsArray,  // Filter to search results if active
          };
          const customFn = ssrmQueryFnRef.current;
          const { rows, lastRow } = customFn
            ? await customFn(queryParams)
            : await chemistryApi.queryMolecules(currentFileId, queryParams);

          console.log('[SSRM] Response:', { rowCount: rows.length, lastRow });
          console.log('[SSRM] First row:', rows[0]);

          // Update total count (this won't cause datasource recreation)
          setTotalRows(lastRow);

          // Extract property columns from first batch (only once)
          if (rows.length > 0 && !columnsExtractedRef.current) {
            columnsExtractedRef.current = true;
            const keys = new Set<string>();
            rows.forEach((mol) => {
              if (mol.properties) {
                Object.keys(mol.properties).forEach((key) => keys.add(key));
              }
            });
            setPropertyColumns(Array.from(keys));

            // Calculate initial column stats for heatmap from first batch
            const stats: Record<string, { min: number; max: number; isNumeric: boolean }> = {};
            keys.forEach((key) => {
              let min = Infinity;
              let max = -Infinity;
              let hasNumeric = false;

              rows.forEach((mol) => {
                const value = mol.properties?.[key];
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                if (!isNaN(numValue) && isFinite(numValue)) {
                  hasNumeric = true;
                  min = Math.min(min, numValue);
                  max = Math.max(max, numValue);
                }
              });

              if (hasNumeric && min !== Infinity && max !== -Infinity) {
                stats[key] = { min, max, isNumeric: true };
              }
            });
            console.log('[SSRM Heatmap] Calculated column stats from first batch:', stats);
            setColumnStats(stats);
          }

          // SUCCESS - this tells AG Grid to render the rows
          params.success({ rowData: rows, rowCount: lastRow });
          setSsrmFirstLoadDone(true);

          // Detect if any molecule has enrichment data (to conditionally show columns)
          // Use truthy checks (not != null) so default DB zeros don't trigger columns
          if (!hasEnrichmentData && rows.some((m: any) =>
            m.ip_status || m.patent_count_exact || m.patent_max_zone ||
            m.target_count || m.pathway_count || m.reaction_count || m.bioactivity_count || m.admet_flags || m.tox_alerts
          )) {
            setHasEnrichmentData(true);
          }

          // Load target counts in background (don't block grid rendering)
          if (loadTargetCountsRef.current) {
            loadTargetCountsRef.current(rows);
          }
        } catch (error) {
          console.error('[SSRM] Error fetching rows:', error);
          params.fail();
        }
      },
    };
  }

  // Heatmap cell style function - green (low) -> yellow (mid) -> red (high)
  // Uses per-column heatmapSettings for min/max thresholds (actual values)
  const getHeatmapCellStyle = useCallback((key: string, value: any) => {
    if (!showHeatmap) return null;

    const stat = columnStats[key];
    if (!stat?.isNumeric) return null;

    // Check if this column has heatmap enabled (default: enabled if no specific setting)
    const columnConfig = heatmapSettings.columns[key];
    if (columnConfig && !columnConfig.enabled) return null;

    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return null;

    // Use per-column thresholds if configured, otherwise use data min/max
    const effectiveMin = columnConfig?.min ?? stat.min;
    const effectiveMax = columnConfig?.max ?? stat.max;

    if (effectiveMax === effectiveMin) return null; // Avoid division by zero

    const { intensity } = heatmapSettings;

    // Clamp value to the threshold range
    const clampedValue = Math.max(effectiveMin, Math.min(effectiveMax, numValue));

    // Calculate ratio within the range
    const range = effectiveMax - effectiveMin;
    const ratio = range > 0 ? (clampedValue - effectiveMin) / range : 0.5;

    // Green (low) -> Yellow (mid) -> Red (high)
    const r = ratio < 0.5 ? Math.round(255 * ratio * 2) : 255;
    const g = ratio < 0.5 ? 255 : Math.round(255 * (1 - ratio) * 2);

    return { backgroundColor: `rgba(${r}, ${g}, 0, ${intensity})` };
  }, [showHeatmap, columnStats, heatmapSettings]);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    if (isClientSide && clientColumns) {
      return clientColumns;
    }

    const baseCols: ColDef[] = [
      {
        field: 'structure',
        headerName: 'Structure',
        width: 150,
        minWidth: 120,
        maxWidth: 250,
        pinned: 'left',
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        resizable: true,
        cellRenderer: (params: any) => (
          <StructureRenderer
            smiles={params.data?.smiles}
            molfile={params.data?.molfile}
            moleculeId={params.data?.id ? Number(params.data.id) : undefined}
            highlightQuery={searchQuery}
            onClick={() => handleStructureClick(params.data)}
          />
        ),
      },
      {
        field: 'smiles',
        headerName: 'SMILES',
        width: 200,
        minWidth: 150,
        maxWidth: 400,
        sortable: true,
        filter: 'agTextColumnFilter',
        hide: true,
        resizable: true,
      },
      // Entity type column hidden — classification is unreliable
      {
        field: 'entity_type',
        headerName: 'Type',
        hide: true,
      },
    ];

    const propCols: ColDef[] = propertyColumns.map((key) => {
      // Always use the original property key as the column header — never wrap in brackets
      const cleanKey = key.replace(/^\[(.+)\]$/, '$1').replace(/^\((.+)\)$/, '$1');
      const unitInfo = columnUnitsMap?.[key];
      const override = unitOverrides[key];
      const displayUnit = override?.displayUnit || unitInfo?.unit;
      const headerLabel = displayUnit && unitInfo?.unitType !== 'dimensionless'
        ? `${cleanKey} (${displayUnit})`
        : cleanKey;

      // Width based on header text length — prevents truncation on first render
      const headerWidth = Math.max(130, (headerLabel?.length || 8) * 12);

      return {
        field: `properties.${key}`,
        headerName: headerLabel,
        width: headerWidth,
        minWidth: 100,
        sortable: true,
        filter: key.match(/^(MW|LogP|TPSA|HBD|HBA|RotBonds|Rings)$/) ? 'agNumberColumnFilter' : 'agTextColumnFilter',
        valueGetter: (params: any) => {
          const raw = params.data?.properties?.[key] ?? '';
          if (override && typeof raw === 'number') {
            // Cross-system conversion: molar ↔ mass (per-row MW)
            const origType = unitInfo?.unitType;
            if (origType === 'concentration_molar' && override.unitType === 'concentration_mass') {
              const mw = params.data?.properties?.['MW'] ?? params.data?.mw;
              if (!mw || mw <= 0) return null;
              return convertMolarToMass(raw, override.originalUnit, override.displayUnit, mw);
            }
            if (origType === 'concentration_mass' && override.unitType === 'concentration_molar') {
              const mw = params.data?.properties?.['MW'] ?? params.data?.mw;
              if (!mw || mw <= 0) return null;
              return convertMassToMolar(raw, override.originalUnit, override.displayUnit, mw);
            }
            // Linear conversion within same group
            return convertValue(raw, override.originalUnit, override.displayUnit, origType || override.unitType);
          }
          return raw;
        },
        valueFormatter: (params: any) => {
          const v = params.value;
          if (v == null || v === '') return '';
          // NaN-ish values (numeric NaN, or Python NaN serialised as the string
          // "nan") render as a literal "nan" in PK columns (CL/Vz/Vss). Blank them.
          if (typeof v === 'number' && isNaN(v)) return '—';
          if (typeof v === 'string' && v.trim().toLowerCase() === 'nan') return '—';
          if (typeof v === 'object') {
            // Extract common nested value patterns (e.g. {value: 123, unit: "nM"})
            if ('value' in v) return String(v.value);
            if ('activity' in v) return String(v.activity);
            return JSON.stringify(v);
          }
          return String(v);
        },
        valueSetter: (params: any) => {
          if (params.data?.properties) {
            params.data.properties[key] = params.newValue;
            return true;
          }
          return false;
        },
        editable: !isLocked,
        cellStyle: (params: any) => {
          const heatmapStyle = getHeatmapCellStyle(key, params.value);
          if (!isLocked) {
            return { ...heatmapStyle, border: '1px solid var(--card-border)' };
          }
          return heatmapStyle;
        },
      };
    });

    // IP Status column (from patent enrichment)
    // IMPORTANT: Use params.value (from field) not params.data - this is the original working pattern
    const ipStatusCol: ColDef = {
      field: 'ip_status',
      headerName: 'IP Status',
      width: 110,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: (params: any) => {
        const status = params.value;
        if (!status) return null;
        const styles: Record<string, { bg: string; text: string }> = {
          clear: { bg: '#dcfce7', text: '#166534' },
          caution: { bg: '#fef3c7', text: '#92400e' },
          crowded: { bg: '#fee2e2', text: '#991b1b' },
          exact: { bg: '#f3e8ff', text: '#6b21a8' },
          error: { bg: 'var(--hover-bg)', text: 'var(--text-secondary)' },
        };
        const style = styles[status] || { bg: 'var(--hover-bg)', text: 'var(--text-primary)' };
        return (
          <span style={{
            backgroundColor: style.bg,
            color: style.text,
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'capitalize',
          }}>
            {status}
          </span>
        );
      },
    };

    // Patent count column
    const patentCountCol: ColDef = {
      field: 'patent_count_exact',
      headerName: 'Patents',
      width: 90,
      minWidth: 80,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: any) => {
        if (params.value == null || params.value === 0) return '';
        return params.value.toLocaleString();
      },
    };

    // Patent zone column (colored dot from enrichment)
    const patentZoneCol: ColDef = {
      field: 'patent_max_zone',
      headerName: 'Zone',
      width: 80,
      minWidth: 70,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        const zone = params.value;
        if (zone == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        const colors: Record<number, string> = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#3b82f6', 5: '#22c55e' };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'patents'); }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[zone] || 'var(--text-secondary)' }} />
            <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{zone}</span>
          </div>
        );
      },
    };

    // Target count column
    const targetCountCol: ColDef = {
      field: 'target_count',
      headerName: 'Targets',
      width: 90,
      minWidth: 80,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        return (
          <span style={{ fontSize: '12px', color: 'var(--text-primary)', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'targets'); }}>
            {params.value}
          </span>
        );
      },
    };

    // Pathway count column
    const pathwayCountCol: ColDef = {
      field: 'pathway_count',
      headerName: 'Pathways',
      width: 95,
      minWidth: 85,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        return (
          <span style={{ fontSize: '12px', color: 'var(--text-primary)', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'pathways'); }}>
            {params.value}
          </span>
        );
      },
    };

    // Reaction count column
    const reactionCountCol: ColDef = {
      field: 'reaction_count',
      headerName: 'Reactions',
      width: 95,
      minWidth: 85,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        return (
          <span style={{ fontSize: '12px', color: 'var(--text-primary)', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'reactions'); }}>
            {params.value}
          </span>
        );
      },
    };

    // Bioactivity count column (ChEMBL reference data)
    const bioactivityCountCol: ColDef = {
      field: 'bioactivity_count',
      headerName: 'Bioactivity',
      width: 100,
      minWidth: 90,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null || params.value === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        return (
          <span style={{
            fontSize: '11px',
            color: '#0D9488',
            fontWeight: 600,
            cursor: onRowClick ? 'pointer' : undefined,
            padding: '1px 6px',
            borderRadius: '8px',
            backgroundColor: 'rgba(13, 148, 136, 0.1)',
          }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'bioactivity'); }}>
            {params.value} assay{params.value !== 1 ? 's' : ''}
          </span>
        );
      },
    };

    // ADMET flags column (count of red ADMET alerts)
    const admetFlagsCol: ColDef = {
      field: 'admet_flags',
      headerName: 'ADMET',
      width: 80,
      minWidth: 70,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        const val = params.value as number;
        const dotColor = val === 0 ? '#10b981' : val <= 2 ? '#f59e0b' : '#ef4444';
        return (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'admet'); }}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-primary)' }}>{val}</span>
          </span>
        );
      },
    };

    // Tox alerts column (count of structural alerts)
    const toxAlertsCol: ColDef = {
      field: 'tox_alerts',
      headerName: 'Tox',
      width: 70,
      minWidth: 60,
      suppressSizeToFit: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value == null) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;
        const val = params.value as number;
        const dotColor = val === 0 ? '#10b981' : val <= 2 ? '#f59e0b' : '#ef4444';
        return (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: onRowClick ? 'pointer' : undefined }}
            onClick={() => { if (onRowClick && params.data) onRowClick(params.data, 'admet'); }}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-primary)' }}>{val}</span>
          </span>
        );
      },
    };

    // Targets column (for linking molecules to protein targets)
    const linkedTargetCol: ColDef = {
      field: 'linked_targets',
      headerName: 'Linked',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: LinkedTargetsRenderer,
    };

    // Actions column with View Details button
    const actionsCol: ColDef = {
      headerName: '',
      width: 50,
      maxWidth: 50,
      sortable: false,
      filter: false,
      pinned: 'left',
      suppressMovable: true,
      cellRenderer: ActionsRenderer,
    };

    // Build derived column definitions (from "Add Converted Column")
    const derivedCols: ColDef[] = derivedColumns.map((dc) => {
      return {
        colId: `derived.${dc.id}`,
        headerName: dc.headerName,
        width: 120,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params: any) => {
          const raw = params.data?.properties?.[dc.sourceField];
          if (raw == null || typeof raw !== 'number') return null;

          if (dc.type === 'logarithmic') {
            const logPair = getLogPartner(dc.sourceField);
            if (logPair?.direction === 'to_log') {
              // Use the unit captured when the derived column was created. Re-reading
              // columnUnitsMap here used to lose inferred/confirmed µM units and silently
              // treat every value as nM (a three-log-unit error).
              return convertToNegLog(raw, dc.sourceUnit || 'nM');
            } else if (logPair?.direction === 'from_log') {
              const targetUnit = getMolarUnitFromLabel(dc.targetUnit) || 'nM';
              return convertFromNegLog(raw, targetUnit);
            }
            return null;
          }

          if (dc.type === 'cross_system') {
            const mw = params.data?.properties?.['MW'] ?? params.data?.mw;
            if (!mw || mw <= 0) return null;
            if (dc.sourceUnitType === 'concentration_molar') {
              return convertMolarToMass(raw, dc.sourceUnit, dc.targetUnit, mw);
            } else {
              return convertMassToMolar(raw, dc.sourceUnit, dc.targetUnit, mw);
            }
          }

          // Linear
          return convertValue(raw, dc.sourceUnit, dc.targetUnit, dc.sourceUnitType);
        },
        valueFormatter: (params: any) => {
          if (params.value == null || isNaN(params.value)) return '—';
          // Smart formatting: more decimals for small numbers
          const v = Math.abs(params.value);
          if (v === 0) return '0';
          if (v >= 100) return params.value.toFixed(1);
          if (v >= 1) return params.value.toFixed(2);
          return params.value.toPrecision(3);
        },
      };
    });

    // Salt form column — shows badge with salt name + stoichiometry when molecule had salt stripped
    const saltFormCol: ColDef = {
      field: 'salt_name',
      headerName: 'Salt Form',
      width: 140,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: (params: any) => {
        const data = params.data;
        if (!data?.is_salt_stripped) return null;
        const parts: string[] = [];
        if (data.salt_name) {
          const mult = data.salt_multiplicity && data.salt_multiplicity !== 1
            ? ` ${data.salt_multiplicity}:1` : '';
          parts.push(`${data.salt_name}${mult}`);
        }
        if (data.solvate_name) parts.push(data.solvate_name);
        if (data.hydrate_count) {
          const hc = data.hydrate_count;
          const prefix = hc === 0.5 ? 'hemi' : hc === 1 ? 'mono' : hc === 2 ? 'di' : hc === 3 ? 'tri' : `${hc}×`;
          parts.push(`${prefix}hydrate`);
        }
        if (parts.length === 0) return null;
        return (
          <span style={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }} title={`Salt: ${data.salt_smiles || '–'}\nParent: ${data.parent_smiles || '–'}`}>
            {parts.join(' · ')}
          </span>
        );
      },
    };

    // Only show enrichment columns when data exists (avoids empty dash columns on fresh uploads)
    const enrichmentCols = hasEnrichmentData
      ? [ipStatusCol, patentCountCol, patentZoneCol, targetCountCol, pathwayCountCol, reactionCountCol, bioactivityCountCol, admetFlagsCol, toxAlertsCol, linkedTargetCol]
      : [];
    // Salt form column is always included — only renders when is_salt_stripped is true
    return [actionsCol, ...baseCols, saltFormCol, ...propCols, ...derivedCols, ...enrichmentCols];
  }, [isClientSide, clientColumns, propertyColumns, searchQuery, handleStructureClick, getHeatmapCellStyle, LinkedTargetsRenderer, isLocked, hasEnrichmentData, columnUnitsMap, unitOverrides, derivedColumns]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: false,
    minWidth: 120,
  }), []);

  // Handle cell value changes - save to backend
  const handleCellValueChanged = useCallback(async (event: any) => {
    const { data, colDef, newValue, oldValue } = event;
    if (newValue === oldValue) return;

    const moleculeId = data?.id;
    if (!moleculeId || !fileId) return;

    // Extract property key from field (e.g., "properties.Activity" -> "Activity")
    const field = colDef.field;
    if (!field?.startsWith('properties.')) return;
    const propertyKey = field.replace('properties.', '');

    console.log('[MoleculeGrid] Cell changed:', { moleculeId, propertyKey, oldValue, newValue });

    try {
      // Update the molecule on the backend
      await chemistryApi.updateMolecule(fileId, moleculeId, {
        properties: { ...data.properties, [propertyKey]: newValue }
      });
      console.log('[MoleculeGrid] Cell edit saved successfully');

      // Add to local edit history for undo (only on successful save)
      editHistoryRef.current.push({
        moleculeId,
        propertyKey,
        oldValue,
        newValue,
        rowData: data,
      });
      // Limit history to last 50 edits
      if (editHistoryRef.current.length > 50) {
        editHistoryRef.current.shift();
      }

      // Notify parent for undo tracking
      if (onCellEdit) {
        onCellEdit(moleculeId, propertyKey, oldValue, newValue);
      }
    } catch (error) {
      console.error('[MoleculeGrid] Failed to save cell edit:', error);
      // Revert the value on error
      if (data.properties) {
        data.properties[propertyKey] = oldValue;
        gridApiRef.current?.refreshCells({ rowNodes: [event.node], force: true });
      }
    }
  }, [fileId, onCellEdit]);

  // Undo last edit (Ctrl+Z)
  const handleUndo = useCallback(async () => {
    if (editHistoryRef.current.length === 0 || !fileId) {
      console.log('[MoleculeGrid] Nothing to undo');
      return;
    }

    const lastEdit = editHistoryRef.current.pop()!;
    const { moleculeId, propertyKey, oldValue } = lastEdit;

    console.log('[MoleculeGrid] Undoing:', { moleculeId, propertyKey, oldValue });

    try {
      // Save only the reverted property to backend (don't spread stale rowData)
      await chemistryApi.updateMolecule(fileId, moleculeId, {
        properties: { [propertyKey]: oldValue }
      });
      console.log('[MoleculeGrid] Undo saved successfully');

      // Refresh grid from server to show correct data
      if (gridApiRef.current) {
        gridApiRef.current.refreshServerSide({ purge: false });
      }
    } catch (error) {
      console.error('[MoleculeGrid] Failed to save undo:', error);
      // Re-add to history if undo failed
      editHistoryRef.current.push(lastEdit);
    }
  }, [fileId]);

  // Keyboard shortcut for undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !isLocked) {
        e.preventDefault();
        handleUndo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, isLocked]);

  // Column resize handler - debounced save to localStorage
  const onColumnResized = useCallback((event: any) => {
    if (!fileId || event.source === 'api') return; // Only save user-initiated resizes

    // Clear existing timer
    if (columnResizeTimerRef.current) {
      clearTimeout(columnResizeTimerRef.current);
    }

    // Debounce: save after 500ms of no resizing
    columnResizeTimerRef.current = setTimeout(() => {
      if (gridApiRef.current) {
        const state = gridApiRef.current.getColumnState();
        saveColumnState(fileId, state);
        // Keep shadow copy of normal-mode widths for transpose restore
        if (!isTransposed) {
          preTransposeColumnStateRef.current = state;
        }
        console.log('[MoleculeGrid] Saved column state for file:', fileId);
      }
    }, 500);
  }, [fileId, isTransposed]);

  // Grid ready handler - set datasource and restore column state
  const focusFrameRef = useRef<number | null>(null);
  const scheduleNavigationFocus = useCallback(() => {
    if (focusFrameRef.current != null) return;
    focusFrameRef.current = requestAnimationFrame(() => {
      focusFrameRef.current = null;
      revealNavigationFocus();
    });
  }, [revealNavigationFocus]);

  useEffect(() => () => {
    if (focusFrameRef.current != null) cancelAnimationFrame(focusFrameRef.current);
  }, []);

  const prepareNavigationFocus = useCallback((api: GridApi) => {
    const focus = navigationFocusRef.current;
    if (!focus || preparedFocusRef.current === focus.requestKey) return;
    preparedFocusRef.current = focus.requestKey;
    completedFocusRef.current = null;
    // An incoming link starts in source-file order, without a stale grid filter.
    // Widths/visibility are preserved and the user's saved column state is not overwritten.
    api.setFilterModel(null);
    api.applyColumnState({ defaultState: { sort: null } });
    if (!isClientSide) api.refreshServerSide({ purge: true });
    scheduleNavigationFocus();
  }, [isClientSide, scheduleNavigationFocus]);

  useEffect(() => {
    if (!navigationFocus || !gridApiRef.current) return;
    prepareNavigationFocus(gridApiRef.current);
    scheduleNavigationFocus();
  }, [navigationFocus, prepareNavigationFocus, scheduleNavigationFocus]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    onGridApiReady?.(params.api);

    if (!isClientSide && datasourceRef.current) {
      console.log('[SSRM] Setting datasource on grid ready');
      params.api.setGridOption('serverSideDatasource', datasourceRef.current);
    }

    // Restore saved column state if available
    if (fileId) {
      const savedState = loadColumnState(fileId);
      if (savedState) {
        console.log('[MoleculeGrid] Restoring saved column state for file:', fileId);
        params.api.applyColumnState({ state: savedState, applyOrder: true });
        // Save to transpose ref for restore after transpose toggle
        if (!isTransposed) {
          preTransposeColumnStateRef.current = savedState;
        }
      }
      // No auto-size here — initial column widths from colDef apply;
      // onFirstDataRendered will auto-size after data loads
    }
    prepareNavigationFocus(params.api);
  }, [isClientSide, fileId, isTransposed, prepareNavigationFocus]);

  // Handle fileId changes - refresh the grid data
  useEffect(() => {
    if (gridApiRef.current && !isClientSide && fileId) {
      console.log('[SSRM] FileId changed, refreshing grid');
      hasAutoSizedRef.current = false;  // Allow auto-size for new file
      columnsExtractedRef.current = false;  // Reset so we extract columns again
      setPropertyColumns([]);
      setColumnStats({});  // Reset heatmap stats
      setSsrmFirstLoadDone(false);
      setHasEnrichmentData(false);
      // Purge cache and refetch
      gridApiRef.current.refreshServerSide({ purge: true });
    }
  }, [fileId, isClientSide]);

  // Handle transpose transitions — restore normal column state when exiting transpose
  useEffect(() => {
    const wasTransposed = prevIsTransposedRef.current;
    prevIsTransposedRef.current = isTransposed;

    if (wasTransposed === isTransposed) return; // No change

    if (wasTransposed && !isTransposed) {
      // Leaving transpose — restore saved normal-mode column state after grid re-renders
      // (normal-mode state is continuously saved via onColumnResized and onFirstDataRendered)
      if (preTransposeColumnStateRef.current) {
        const stateToRestore = preTransposeColumnStateRef.current;
        // Delay to let AG Grid finish re-rendering with the new (non-transposed) columnDefs
        setTimeout(() => {
          if (gridApiRef.current) {
            gridApiRef.current.applyColumnState({ state: stateToRestore, applyOrder: true });
            console.log('[MoleculeGrid] Restored column state after transpose');
          }
        }, 300);
      }
    }
  }, [isTransposed]);

  // Handle matchIds changes - refresh grid when search results change
  useEffect(() => {
    if (gridApiRef.current && !isClientSide) {
      console.log('[SSRM] matchIds changed, refreshing grid with', matchIds?.size || 0, 'matches');
      // Purge cache and refetch with new filter
      gridApiRef.current.refreshServerSide({ purge: true });
    }
  }, [matchIds, isClientSide]);

  // Handle refreshTrigger changes - refresh grid after enrichment or external data changes
  useEffect(() => {
    if (!refreshTrigger) return;

    if (isClientSide) {
      // Client-side mode: tell parent to re-fetch data (with enrichment fields)
      console.log('[ClientSide] refreshTrigger changed, re-fetching data');
      if (onMoleculeUpdate) onMoleculeUpdate();
    } else if (gridApiRef.current) {
      console.log('[SSRM] refreshTrigger changed, refreshing grid');
      columnsExtractedRef.current = false;
      gridApiRef.current.refreshServerSide({ purge: true });
    }

    // Auto-size all columns (including new enrichment columns) after data loads
    setTimeout(() => {
      const api = gridApiRef.current;
      if (!api) return;
      const allColIds = api.getColumns()?.map((c: any) => c.getColId()) || [];
      if (allColIds.length > 0) {
        api.autoSizeColumns(allColIds, false);
      }
    }, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, isClientSide]);

  // Fetch entity type counts when fileId changes (skip non-numeric IDs like "project-4")
  useEffect(() => {
    if (!fileId || isNaN(parseInt(fileId, 10))) return;
    chemistryApi.getEntityTypeCounts(fileId).then((data) => {
      setEntityTypeCounts(data.entity_types || {});
    }).catch(() => {});
  }, [fileId, refreshTrigger]);

  // Click-away handler to close entity type context menu
  useEffect(() => {
    if (!entityTypeMenu) return;
    const handler = () => setEntityTypeMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [entityTypeMenu]);

  // Redraw rows when selected compound changes (for row highlight)
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.redrawRows();
    }
  }, [selectedCompoundId]);

  // Handle resetColumnsTrigger - clear saved state and apply sensible default widths
  useEffect(() => {
    if (resetColumnsTrigger && gridApiRef.current && fileId) {
      console.log('[MoleculeGrid] Resetting column widths for file:', fileId);
      clearColumnState(fileId);

      const NUMERIC_COLS = new Set(['MW', 'LogP', 'TPSA', 'HBD', 'HBA', 'RotBonds', 'Rings', 'FractionCSP3', 'NumAromaticRings']);
      const TEXT_COLS = new Set(['Name', 'SMILES', 'Formula', 'ChEMBL ID', 'Molecule Name', 'Compound Name', 'InChIKey', 'CAS']);

      const allCols = gridApiRef.current.getColumns();
      if (allCols) {
        const stateUpdates = allCols.map((col) => {
          const colId = col.getColId();
          const headerName = (col.getColDef() as any).headerName || '';

          // Checkbox / selection column
          if (!colId || colId === '0') return { colId, width: 50, flex: null, hide: false };
          // Actions column (icons)
          if (headerName === '' && colId !== 'structure' && colId !== 'smiles') return { colId, width: 90, flex: null, hide: false };
          // Structure column
          if (colId === 'structure') return { colId, width: 150, flex: null, hide: false };
          // Enrichment icon columns (IP Status, Zone, Patents, Targets, Pathways, Reactions, Linked)
          if (['ip_status', 'patent_max_zone', 'patent_count_exact', 'target_count', 'pathway_count', 'reaction_count', 'linked_targets', 'admet_flags', 'tox_alerts'].includes(colId)) {
            return { colId, width: 100, flex: null, hide: false };
          }

          // Property columns — extract the key name from "properties.XYZ"
          const propKey = colId.startsWith('properties.') ? colId.replace('properties.', '') : headerName;
          if (NUMERIC_COLS.has(propKey)) return { colId, width: 100, flex: null, hide: false };
          if (TEXT_COLS.has(propKey)) return { colId, width: 150, flex: null, hide: false };

          // Default for unknown property columns
          return { colId, width: 120, flex: null, hide: false };
        });

        gridApiRef.current.applyColumnState({ state: stateUpdates });
      }
    }
  }, [resetColumnsTrigger, fileId]);

  // Handle selectAllTrigger - select all visible rows
  useEffect(() => {
    if (selectAllTrigger && gridApiRef.current) {
      gridApiRef.current.selectAll();
    }
  }, [selectAllTrigger]);

  // Handle deselectAllTrigger - deselect all rows
  useEffect(() => {
    if (deselectAllTrigger && gridApiRef.current) {
      gridApiRef.current.deselectAll();
    }
  }, [deselectAllTrigger]);

  // Handle columnVisibilityTrigger - show/hide a column via AG Grid API
  useEffect(() => {
    if (columnVisibilityTrigger && gridApiRef.current && columnVisibilityAction) {
      const colId = columnVisibilityAction.columnId.startsWith('properties.')
        ? columnVisibilityAction.columnId
        : `properties.${columnVisibilityAction.columnId}`;
      gridApiRef.current.applyColumnState({
        state: [{ colId, hide: !columnVisibilityAction.visible }],
      });
      // Persist column state so hidden columns stay hidden after refresh
      if (fileId) {
        const state = gridApiRef.current.getColumnState();
        saveColumnState(fileId, state);
      }
    }
  }, [columnVisibilityTrigger, columnVisibilityAction, fileId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading molecules...</div>
      </div>
    );
  }

  if (isClientSide && (!clientData || clientData.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <div className="w-16 h-16 mb-4 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center">
          <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <circle cx="12" cy="12" r="3" /><circle cx="19" cy="5" r="2" /><circle cx="5" cy="5" r="2" />
            <line x1="14.5" y1="9.5" x2="17.5" y2="6.5" /><line x1="9.5" y1="9.5" x2="6.5" y2="6.5" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-600">No molecules found in this file</p>
        <p className="text-sm text-gray-400 mt-1">The file may be empty or in an unsupported format.</p>
      </div>
    );
  }

  // Debug logging before render
  console.log('[MoleculeGrid] Rendering grid:', {
    isClientSide,
    clientColumnsProvided: !!clientColumns,
    clientDataLength: clientData?.length,
    clientDataFirstItem: clientData?.[0] ? Object.keys(clientData[0]) : 'N/A',
    propertyColumnsCount: propertyColumns.length,
    columnDefsCount: columnDefs.length,
  });

  return (
    <>
      {!isClientSide && totalRows > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-600">
          {totalRows.toLocaleString()} molecules
        </div>
      )}

      {!isClientSide && ssrmFirstLoadDone && totalRows === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <div className="w-16 h-16 mb-4 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center">
            <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <circle cx="12" cy="12" r="3" /><circle cx="19" cy="5" r="2" /><circle cx="5" cy="5" r="2" />
              <line x1="14.5" y1="9.5" x2="17.5" y2="6.5" /><line x1="9.5" y1="9.5" x2="6.5" y2="6.5" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-gray-600">No molecules found in this file</p>
          <p className="text-sm text-gray-400 mt-1">The file may be empty or in an unsupported format.</p>
        </div>
      )}

      {/* Entity Type Filter Bar — hidden: classification is unreliable */}

      <div className="ag-theme-alpine molecule-grid w-full h-full" style={!isClientSide && ssrmFirstLoadDone && totalRows === 0 ? { display: 'none' } : undefined}>
        <style>{`
          .molecule-grid.ag-theme-alpine {
            --ag-header-background-color: var(--hover-bg);
            --ag-header-foreground-color: var(--text-secondary);
            --ag-border-color: var(--hover-bg);
            --ag-row-border-color: transparent;
            --ag-selected-row-background-color: #ecfdf5;
            --ag-row-hover-color: #f0fdf4;
            --ag-font-family: inherit;
          }
          .molecule-grid .ag-header-cell-label {
            font-weight: 600;
            font-size: 0.75rem;
            letter-spacing: 0.05em;
          }
          .molecule-grid .ag-cell {
            display: flex;
            align-items: center;
            border-right: none !important;
          }
          .molecule-grid .ag-row {
            border-bottom: 1px solid var(--hover-bg);
          }
          .molecule-grid .ag-row:hover {
            background-color: #f0fdf4 !important;
          }
          .molecule-grid .ag-row-selected {
            background-color: #ecfdf5 !important;
          }
          .molecule-grid .ag-header {
            border-bottom: 2px solid var(--card-border);
          }
          .molecule-grid .ag-cell-inline-editing {
            border: 2px solid #0D9488 !important;
            background-color: rgba(13, 148, 136, 0.05) !important;
            border-radius: 2px;
            box-shadow: 0 0 0 1px rgba(13, 148, 136, 0.2);
          }
          .molecule-grid .ag-cell-inline-editing input {
            cursor: text;
          }
          .molecule-grid .ag-cell.ag-cell-editable:hover {
            cursor: text;
            background-color: rgba(13, 148, 136, 0.03);
          }
          .molecule-grid .ag-checkbox-input-wrapper {
            width: 20px;
            height: 20px;
          }
          .molecule-grid .ag-checkbox-input-wrapper input {
            width: 20px;
            height: 20px;
            accent-color: #10b981;
            cursor: pointer;
          }
          .molecule-grid .ag-checkbox-input-wrapper::after {
            color: #10b981;
          }
          .molecule-grid .ag-checkbox-input-wrapper.ag-checked::after {
            color: #10b981;
          }
          .molecule-grid .ag-selection-checkbox {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .molecule-grid .mol-col-header {
            padding: 2px;
          }
        `}</style>
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType={isClientSide ? 'clientSide' : 'serverSide'}
          rowData={isClientSide ? clientData : undefined}
          // DO NOT pass serverSideDatasource as prop - only set via API in onGridReady
          selectionColumnDef={{
            pinned: 'left',
            width: 48,
            maxWidth: 48,
            minWidth: 48,
            suppressMovable: true,
            suppressSizeToFit: true,
          }}
          rowSelection={{
            mode: 'multiRow',
            selectAll: 'currentPage',
            headerCheckbox: true,
            enableClickSelection: false,
          }}
          onGridReady={onGridReady}
          onModelUpdated={scheduleNavigationFocus}
          onFirstDataRendered={(params: any) => {
            // Auto-size all columns once on first data render only
            if (hasAutoSizedRef.current) return;
            hasAutoSizedRef.current = true;
            if (fileId && loadColumnState(fileId)) return; // Skip if user has saved column state
            // Delay to let AG Grid finish rendering visible cells in the DOM
            setTimeout(() => {
              const allColIds = params.api.getColumns()?.map((c: any) => c.getColId()) || [];
              if (allColIds.length > 0) {
                params.api.autoSizeColumns(allColIds, false);
              }
              // Save normal-mode state for transpose restore
              if (!isTransposed) {
                preTransposeColumnStateRef.current = params.api.getColumnState();
              }
            }, 300);
          }}
          onSelectionChanged={onSelectionChanged}
          getRowId={isClientSide
            ? (clientColumns
                // Transposed view - row ID is the property name
                ? (params) => params.data.property
                // Search results in client-side mode - row ID is molecule id
                : (params) => String(params.data?.id ?? params.data?.mol_index ?? Math.random()))
            : (params) => {
                // SSRM mode - search results may use different ID field names
                const id = params.data?.id || params.data?.mol_id || params.data?.molecule_id || params.data?.mol_index;
                if (id === undefined || id === null) {
                  console.warn('[MoleculeGrid] Row missing ID, data keys:', Object.keys(params.data || {}));
                  return `row-${params.data?.mol_index || Math.random()}`;
                }
                return String(id);
              }}
          getRowStyle={getRowStyle}
          animateRows={false}
          rowHeight={isClientSide ? 110 : 120}
          headerHeight={isTransposed ? 80 : 45}
          // AG Grid can only measure rendered cells when auto-sizing. Keep every
          // horizontal column rendered so "Auto size all columns" also measures
          // columns that are currently outside the viewport.
          suppressColumnVirtualisation={true}
          cacheBlockSize={100}
          maxBlocksInCache={10}
          blockLoadDebounceMillis={200}
          context={{ onViewDetails: handleViewDetails, onRowClick, fileId, fileName }}
          onCellValueChanged={handleCellValueChanged}
          onCellClicked={(event) => {
            if (event.colDef?.field === 'structure' && event.data) {
              handleStructureClick(event.data as Molecule);
            }
          }}
          onRowClicked={(event) => {
            // Skip clicks on checkbox, actions, or structure cells
            const target = event.event?.target as HTMLElement;
            if (target?.closest('[col-id="checkbox"], [col-id="actions"], [col-id="structure"]')) return;
            if (target?.closest('button, a, input')) return;
            // In edit mode, clicking a property cell should start inline editing, not open detail panel
            const cellEl = target?.closest('[col-id]') as HTMLElement | null;
            const colId = cellEl?.getAttribute('col-id') || '';
            if (!isLocked && colId.startsWith('properties.')) return;
            if (event.data && onRowClick) {
              onRowClick(event.data as Molecule);
            }
          }}
          onColumnResized={onColumnResized}
          onCellContextMenu={(event: any) => {
            if (event.colDef?.field === 'entity_type' && event.data) {
              event.event?.preventDefault();
              const mouseEvent = event.event as MouseEvent;
              setEntityTypeMenu({
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
                moleculeId: parseInt(event.data.id),
                currentType: event.data.entity_type || 'small_molecule',
              });
            }
          }}
          getMainMenuItems={(params: any) => {
            const defaultItems = params.defaultItems || [];
            const colId = params.column?.getColId?.() || '';
            const propName = colId.startsWith('properties.') ? colId.substring(11) : '';
            if (!propName) return defaultItems;

            // Try columnUnitsMap first, then fall back to inference from column name
            let unitInfo = columnUnitsMap?.[propName];
            if (!unitInfo?.unitType) {
              const inferred = inferUnits([propName])[0];
              if (inferred && inferred.unitType !== 'unspecified') {
                unitInfo = { unit: inferred.unit, unitType: inferred.unitType, displayName: null };
              }
            }
            console.log('[ConvertMenu]', propName, 'unit:', unitInfo?.unit, 'type:', unitInfo?.unitType, 'hasMW:', propertyColumns.includes('MW'));

            if (!unitInfo?.unitType || unitInfo.unitType === 'dimensionless' || unitInfo.unitType === 'percentage' || unitInfo.unitType === 'area') {
              // Even dimensionless fields might have log partners (pIC50 → IC50)
              const logPartner = getLogPartner(propName);
              if (!logPartner) return defaultItems;
              // Show only the "Add column" option for log conversion
              return [
                ...defaultItems,
                'separator',
                {
                  name: 'Add Converted Column',
                  subMenu: [{
                    name: logPartner.partner,
                    action: () => {
                      const id = `${propName}__${logPartner.partner.replace(/[^a-zA-Z0-9]/g, '')}`;
                      if (derivedColumns.some(dc => dc.id === id)) return;
                      snapshotColumnWidths();
                      setDerivedColumns(prev => [...prev, {
                        id,
                        sourceField: propName,
                        headerName: `${logPartner.partner} (from ${propName})`,
                        targetUnit: logPartner.partner,
                        type: 'logarithmic',
                        needsMW: false,
                        sourceUnit: unitInfo?.unit || '',
                        sourceUnitType: unitInfo?.unitType || 'dimensionless',
                      }]);
                    },
                  }],
                },
              ];
            }

            // At this point unitInfo is guaranteed defined with a valid unitType
            const confirmedUnitInfo = unitInfo!;

            // Check if MW is available (for cross-system conversions)
            const hasMW = propertyColumns.includes('MW');
            const allConversions = getAvailableConversions(
              confirmedUnitInfo.unit, confirmedUnitInfo.unitType, propName, hasMW
            );
            const displayOptions = allConversions.filter(o => !o.isAddColumn);
            const addColOptions = allConversions.filter(o => o.isAddColumn);

            const currentDisplay = unitOverrides[propName]?.displayUnit || confirmedUnitInfo.unit;
            const menuItems: any[] = [...defaultItems];

            // Section 1: Convert Display (in-place toggle)
            if (displayOptions.length > 0) {
              const displaySubmenu: any[] = [];

              // Linear conversions first
              const linearOpts = displayOptions.filter(o => o.type === 'linear');
              for (const opt of linearOpts) {
                displaySubmenu.push({
                  name: opt.targetUnit,
                  checked: currentDisplay === opt.targetUnit,
                  action: () => {
                    if (opt.targetUnit === confirmedUnitInfo.unit) {
                      setUnitOverrides(prev => {
                        const next = { ...prev };
                        delete next[propName];
                        return next;
                      });
                    } else {
                      setUnitOverrides(prev => ({
                        ...prev,
                        [propName]: {
                          originalUnit: confirmedUnitInfo.unit!,
                          displayUnit: opt.targetUnit,
                          unitType: confirmedUnitInfo.unitType!,
                        },
                      }));
                    }
                    params.api?.refreshCells({ columns: [colId] });
                  },
                });
              }

              // Cross-system conversions (with separator)
              const crossOpts = displayOptions.filter(o => o.type === 'cross_system');
              if (crossOpts.length > 0 && linearOpts.length > 0) {
                displaySubmenu.push('separator');
              }
              for (const opt of crossOpts) {
                displaySubmenu.push({
                  name: `${opt.targetUnit} (needs MW)`,
                  checked: currentDisplay === opt.targetUnit,
                  action: () => {
                    setUnitOverrides(prev => ({
                      ...prev,
                      [propName]: {
                        originalUnit: confirmedUnitInfo.unit!,
                        displayUnit: opt.targetUnit,
                        unitType: opt.type === 'cross_system'
                          ? (confirmedUnitInfo.unitType === 'concentration_molar' ? 'concentration_mass' : 'concentration_molar')
                          : confirmedUnitInfo.unitType!,
                      },
                    }));
                    params.api?.refreshCells({ columns: [colId] });
                  },
                });
              }

              // Reset option
              if (displaySubmenu.length > 0) {
                displaySubmenu.push('separator');
                displaySubmenu.push({
                  name: `${confirmedUnitInfo.unit} (original)`,
                  checked: currentDisplay === confirmedUnitInfo.unit,
                  action: () => {
                    setUnitOverrides(prev => {
                      const next = { ...prev };
                      delete next[propName];
                      return next;
                    });
                    params.api?.refreshCells({ columns: [colId] });
                  },
                });
              }

              menuItems.push('separator');
              menuItems.push({
                name: 'Convert Display',
                subMenu: displaySubmenu,
              });
            }

            // Section 2: Add Converted Column
            if (addColOptions.length > 0) {
              const addColSubmenu: any[] = [];
              for (const opt of addColOptions) {
                const suffix = opt.needsMW ? ' (needs MW)' : '';
                const logLabel = opt.type === 'logarithmic' ? ' (log)' : '';
                addColSubmenu.push({
                  name: `${opt.targetUnit}${suffix}${logLabel}`,
                  action: () => {
                    const id = `${propName}__${opt.targetUnit.replace(/[^a-zA-Z0-9]/g, '')}`;
                    if (derivedColumns.some(dc => dc.id === id)) return; // Already added
                    snapshotColumnWidths();
                    setDerivedColumns(prev => [...prev, {
                      id,
                      sourceField: propName,
                      headerName: `${opt.targetUnit} (from ${propName})`,
                      targetUnit: opt.targetUnit,
                      type: opt.type,
                      needsMW: opt.needsMW,
                      sourceUnit: confirmedUnitInfo.unit || '',
                      sourceUnitType: confirmedUnitInfo.unitType || '',
                    }]);
                  },
                });
              }

              // Option to remove derived columns from this source
              const existingDerived = derivedColumns.filter(dc => dc.sourceField === propName);
              if (existingDerived.length > 0) {
                addColSubmenu.push('separator');
                for (const dc of existingDerived) {
                  addColSubmenu.push({
                    name: `Remove: ${dc.headerName}`,
                    action: () => {
                      snapshotColumnWidths();
                      setDerivedColumns(prev => prev.filter(d => d.id !== dc.id));
                    },
                  });
                }
              }

              menuItems.push({
                name: 'Add Converted Column',
                subMenu: addColSubmenu,
              });
            }

            return menuItems;
          }}
        />
      </div>

      {editorOpen && selectedMolecule && (
        <StructureEditorModal
          isOpen={editorOpen}
          onClose={handleCloseEditor}
          onSave={fileId ? handleSaveStructure : undefined}
          initialStructure={selectedMolecule.molfile || selectedMolecule.smiles || ''}
          title={getMoleculeName(selectedMolecule.properties) || (fileId ? "Edit Structure" : "View Structure")}
          moleculeName={getMoleculeName(selectedMolecule.properties) || undefined}
        />
      )}

      {/* StructureViewerModal removed — click now opens Ketcher directly */}

      {/* Link Target Modal */}
      {linkModalMolecule && (
        <LinkTargetModal
          isOpen={linkModalOpen}
          onClose={handleCloseLinkModal}
          moleculeId={linkModalMolecule.id}
          moleculeSmiles={linkModalMolecule.smiles}
          onLinksUpdated={handleLinksUpdated}
        />
      )}

      {/* Entity Type Context Menu — hidden: classification is unreliable */}
    </>
  );
};

export default MoleculeGrid;
